"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Header } from "@/modules/shared/components/Header";
import { Footer } from "@/modules/shared/components/Footer";
import { PageTransition, FadeIn } from "@/modules/shared/components/PageTransition";
import { siteConfig } from "@/config/site.config";

export default function CirculoClient() {
  // Form State
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [carreraCiclo, setCarreraCiclo] = useState("");
  const [modalidad, setModalidad] = useState<"presencial" | "virtual" | "ambas">("ambas");
  const [interesLectura, setInteresLectura] = useState("");
  const [aceptaPolitica, setAceptaPolitica] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    if (!aceptaPolitica) {
      setErrorMessage("Por favor acepta la política de privacidad para inscribirte.");
      return;
    }
    if (!nombre || !email || !whatsapp) {
      setErrorMessage("Por favor completa tu nombre, correo y WhatsApp.");
      return;
    }

    setFormStatus("loading");
    setErrorMessage("");

    try {
      const formData = new FormData();
      formData.append("nombre", nombre);
      formData.append("email", email);
      formData.append("whatsapp", whatsapp);
      formData.append("carrera_ciclo", carreraCiclo || "No especificado / No es estudiante");
      formData.append("modalidad", modalidad);
      formData.append("interes_lectura", interesLectura || "Sin preferencia específica");
      formData.append("consentimiento_ley_29733", "Aceptado");
      formData.append("_subject", "Nueva Inscripción: Círculo de Estudio Copleston");
      formData.append("_captcha", "false");

      const res = await fetch(`https://formsubmit.co/ajax/${siteConfig.correoContacto}`, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });

      if (res.ok) {
        setFormStatus("success");
      } else {
        setFormStatus("error");
        setErrorMessage("Hubo un error al procesar tu inscripción. Por favor inténtalo nuevamente.");
      }
    } catch {
      setFormStatus("error");
      setErrorMessage("Error de red. Por favor verifica tu conexión o escríbenos directamente.");
    }
  };

  const handleShareWhatsApp = () => {
    const textEncoded = encodeURIComponent(siteConfig.circulo.whatsappShareText);
    window.open(`https://api.whatsapp.com/send?text=${textEncoded}`, "_blank");
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-bg-main text-text-pure flex flex-col font-sans selection:bg-gold/30 selection:text-gold-light">
        <Header />

        <main className="flex-grow pt-24 md:pt-32 pb-20">
          
          {/* HERO */}
          <section className="relative w-full min-h-[55vh] flex items-center justify-center px-6 md:px-12 mb-16 overflow-hidden">
            <div className="absolute inset-0 z-0">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(197,160,89,0.12),transparent_70%)] pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-b from-bg-main/60 via-transparent to-bg-main" />
            </div>

            <div className="relative z-10 text-center max-w-4xl mx-auto pt-8">
              <FadeIn>
                <div className="inline-flex items-center gap-2 border border-gold/35 bg-gold/5 px-4 py-1.5 rounded-full mb-6">
                  <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                  <span className="text-[10px] md:text-xs font-mono tracking-widest text-gold uppercase font-medium">
                    {siteConfig.circulo.estado}
                  </span>
                </div>

                <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl tracking-wide font-light mb-6">
                  Círculo de <span className="font-playfair italic text-gold">Estudio Copleston</span>
                </h1>

                <p className="text-text-muted/95 text-base sm:text-lg md:text-xl max-w-2xl mx-auto font-light leading-relaxed mb-8">
                  Leemos, discutimos y pensamos juntos. Gratis, presencial en Cusco y virtual.
                </p>

                <div className="flex flex-wrap items-center justify-center gap-4 text-xs font-mono text-gold/80">
                  <span className="px-3 py-1 rounded bg-bg-secondary/40 border border-gold/15">
                    {siteConfig.circulo.frecuencia}
                  </span>
                  <span className="px-3 py-1 rounded bg-bg-secondary/40 border border-gold/15">
                    {siteConfig.circulo.diaHora}
                  </span>
                  <span className="px-3 py-1 rounded bg-bg-secondary/40 border border-gold/15">
                    {siteConfig.ubicacion}
                  </span>
                </div>
              </FadeIn>
            </div>
          </section>

          <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-24">
            
            {/* PARA QUIÉN ES */}
            <FadeIn delay={0.1}>
              <section className="p-8 sm:p-12 rounded-2xl border border-gold/20 bg-bg-secondary/30 relative overflow-hidden">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-[1px] bg-gold/50" />
                  <span className="text-[10px] md:text-xs font-semibold tracking-[0.4em] text-gold uppercase">
                    Comunidad
                  </span>
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl text-text-pure font-light mb-6">
                  ¿Para quién es este espacio?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-text-muted text-sm sm:text-base font-light leading-relaxed">
                  <p>
                    Está pensado para <span className="text-text-pure font-normal">estudiantes de Filosofía de la UNSAAC</span>, postulantes a la carrera, estudiantes de Derecho, Psicología y otras disciplinas, así como cualquier persona interesada en leer con profundidad y rigor.
                  </p>
                  <p>
                    <span className="text-gold font-normal">No se necesita saber filosofía previa</span> ni dominar jerga técnica. Lo único indispensable es leer el texto asignado antes de cada reunión y participar con honestidad intelectual y respeto hacia las opiniones ajenas.
                  </p>
                </div>
              </section>
            </FadeIn>

            {/* CÓMO FUNCIONA (3 PASOS) */}
            <FadeIn delay={0.2}>
              <section>
                <div className="text-center flex flex-col items-center mb-12">
                  <span className="text-[10px] md:text-xs font-semibold tracking-[0.4em] text-gold uppercase">
                    Metodología
                  </span>
                  <h2 className="font-serif text-3xl sm:text-4xl text-text-pure mt-3 font-light">
                    ¿Cómo funciona cada sesión?
                  </h2>
                  <div className="w-12 h-[1px] bg-gold/50 mt-4" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-8 rounded-xl border border-gold/15 bg-bg-secondary/20 hover:border-gold/40 transition-colors">
                    <span className="font-serif text-3xl text-gold/60 block mb-4">I</span>
                    <h3 className="font-serif text-xl text-text-pure font-light mb-3">1. Lectura Previa</h3>
                    <p className="text-text-muted text-sm font-light leading-relaxed">
                      Cada participante lee el texto acordado antes de la reunión. Proveemos enlaces de dominio público y guías breves de lectura crítica.
                    </p>
                  </div>

                  <div className="p-8 rounded-xl border border-gold/15 bg-bg-secondary/20 hover:border-gold/40 transition-colors">
                    <span className="font-serif text-3xl text-gold/60 block mb-4">II</span>
                    <h3 className="font-serif text-xl text-text-pure font-light mb-3">2. Exposición Breve</h3>
                    <p className="text-text-muted text-sm font-light leading-relaxed">
                      Un miembro voluntario expone en 15 minutos las tesis centrales del autor, los conceptos clave y las preguntas a debatir.
                    </p>
                  </div>

                  <div className="p-8 rounded-xl border border-gold/15 bg-bg-secondary/20 hover:border-gold/40 transition-colors">
                    <span className="font-serif text-3xl text-gold/60 block mb-4">III</span>
                    <h3 className="font-serif text-xl text-text-pure font-light mb-3">3. Debate y Rigor</h3>
                    <p className="text-text-muted text-sm font-light leading-relaxed">
                      Abrimos la conversación: formulamos objeciones, aclaramos pasajes difíciles y examinamos la vigencia del pensamiento analizado.
                    </p>
                  </div>
                </div>
              </section>
            </FadeIn>

            {/* PRIMERA LECTURA */}
            <FadeIn delay={0.25}>
              <section className="p-8 sm:p-12 rounded-2xl border border-gold/25 bg-[#0a0d17] relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 w-80 h-80 bg-gold/5 blur-3xl pointer-events-none" />
                
                <span className="text-[10px] font-mono tracking-widest uppercase text-gold border border-gold/30 bg-gold/10 px-3 py-1 rounded-sm inline-block mb-4">
                  Primera Lectura Propuesta
                </span>

                <h2 className="font-serif text-3xl sm:text-4xl text-text-pure font-light mb-2">
                  {siteConfig.circulo.primeraLectura.titulo}
                </h2>
                <p className="text-gold text-sm tracking-widest uppercase mb-6 font-medium">
                  {siteConfig.circulo.primeraLectura.autor}
                </p>

                <p className="text-text-pure/85 text-sm sm:text-base font-light leading-relaxed max-w-3xl mb-8">
                  {siteConfig.circulo.primeraLectura.descripcion}
                </p>

                <div className="flex flex-wrap items-center gap-4">
                  <a
                    href={siteConfig.circulo.primeraLectura.enlaceDominioPublico}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-gold/50 bg-gold/10 hover:bg-gold/20 px-6 py-3 text-xs font-sans text-gold uppercase tracking-[0.2em] rounded transition-all duration-300"
                  >
                    <span>Leer Edición de Dominio Público</span>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </section>
            </FadeIn>

            {/* RUTA DE LECTURAS DEL CICLO */}
            <FadeIn delay={0.3}>
              <section>
                <div className="text-center flex flex-col items-center mb-12">
                  <span className="text-[10px] md:text-xs font-semibold tracking-[0.4em] text-gold uppercase">
                    Itinerario Formativo
                  </span>
                  <h2 className="font-serif text-3xl sm:text-4xl text-text-pure mt-3 font-light">
                    Ruta de Lecturas del Primer Ciclo
                  </h2>
                  <p className="text-text-muted text-xs sm:text-sm font-light mt-2">
                    Siguiendo el orden de la Historia de la Filosofía de Frederick Copleston
                  </p>
                  <div className="w-12 h-[1px] bg-gold/50 mt-4" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {siteConfig.circulo.rutaCiclo.map((etapa) => (
                    <div
                      key={etapa.fase}
                      className="p-8 rounded-xl border border-gold/15 bg-bg-secondary/20 relative"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className="font-serif text-2xl text-gold font-light">{etapa.fase}</span>
                        <span className="text-[10px] font-mono tracking-widest text-text-muted uppercase">Etapa</span>
                      </div>
                      <h3 className="font-serif text-lg text-text-pure font-light mb-1">{etapa.titulo}</h3>
                      <p className="text-gold/90 text-xs font-mono mb-3">{etapa.obras}</p>
                      <p className="text-text-muted text-xs leading-relaxed font-light">{etapa.descripcion}</p>
                    </div>
                  ))}
                </div>
              </section>
            </FadeIn>

            {/* FORMULARIO "QUIERO UNIRME" */}
            <FadeIn delay={0.35}>
              <section id="unirme" className="p-8 sm:p-12 md:p-16 rounded-2xl border border-gold/30 bg-[#080b14] shadow-2xl relative">
                <div className="text-center flex flex-col items-center mb-10">
                  <span className="text-[10px] font-semibold tracking-[0.4em] text-gold uppercase">
                    Inscripción Gratuita
                  </span>
                  <h2 className="font-serif text-3xl sm:text-4xl text-text-pure mt-3 font-light">
                    Quiero Unirme al Círculo
                  </h2>
                  <p className="text-text-muted text-xs sm:text-sm font-light max-w-xl mt-2 leading-relaxed">
                    Completa tus datos para coordinar las sesiones y recibir las lecturas. Al enviar, tendrás acceso directo al grupo de WhatsApp.
                  </p>
                  <div className="w-12 h-[1px] bg-gold/50 mt-4" />
                </div>

                {formStatus === "success" ? (
                  <div className="p-8 rounded-xl bg-gold/10 border border-gold/30 text-center space-y-6 animate-fade-in">
                    <div className="w-16 h-16 mx-auto rounded-full bg-gold/20 flex items-center justify-center text-gold text-2xl">
                      ✓
                    </div>
                    <h3 className="font-serif text-2xl text-text-pure font-light">
                      ¡Inscripción Recibida con Éxito!
                    </h3>
                    <p className="text-text-muted text-sm font-light max-w-md mx-auto leading-relaxed">
                      Bienvenido/a al Círculo de Estudio Copleston. Da el siguiente paso y únete al grupo oficial de WhatsApp para presentarte y coordinar la primera sesión:
                    </p>
                    <a
                      href={siteConfig.circulo.whatsappGroupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#25D366] text-black font-semibold px-8 py-3.5 text-xs uppercase tracking-widest rounded shadow-lg hover:brightness-110 transition-all duration-300"
                    >
                      <span>Entrar al Grupo de WhatsApp</span>
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                        <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.007c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86.174.086.275.072.376-.044.101-.116.433-.506.549-.68.116-.173.231-.145.39-.086s1.011.477 1.184.564.289.13.332.203c.044.072.044.42-.1.825z" />
                      </svg>
                    </a>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto text-left">
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="_honey"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      className="hidden"
                      tabIndex={-1}
                      autoComplete="off"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-mono uppercase text-gold/80 mb-2">Nombre y Apellidos *</label>
                        <input
                          type="text"
                          value={nombre}
                          onChange={(e) => setNombre(e.target.value)}
                          required
                          placeholder="Tu nombre completo"
                          className="w-full px-4 py-3 bg-bg-main border border-gold/20 focus:border-gold text-text-pure text-sm outline-none rounded font-light"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-mono uppercase text-gold/80 mb-2">Correo Electrónico *</label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          placeholder="tu@correo.com"
                          className="w-full px-4 py-3 bg-bg-main border border-gold/20 focus:border-gold text-text-pure text-sm outline-none rounded font-light"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-mono uppercase text-gold/80 mb-2">WhatsApp / Teléfono *</label>
                        <input
                          type="tel"
                          value={whatsapp}
                          onChange={(e) => setWhatsapp(e.target.value)}
                          required
                          placeholder="+51 984 000 000"
                          className="w-full px-4 py-3 bg-bg-main border border-gold/20 focus:border-gold text-text-pure text-sm outline-none rounded font-light"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-mono uppercase text-gold/80 mb-2">Carrera y Ciclo (o &quot;No soy estudiante&quot;)</label>
                        <input
                          type="text"
                          value={carreraCiclo}
                          onChange={(e) => setCarreraCiclo(e.target.value)}
                          placeholder="Ej. Filosofía 4to ciclo / Derecho / Autodidacta"
                          className="w-full px-4 py-3 bg-bg-main border border-gold/20 focus:border-gold text-text-pure text-sm outline-none rounded font-light"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-mono uppercase text-gold/80 mb-2">Modalidad de Asistencia Preferida</label>
                      <div className="grid grid-cols-3 gap-3">
                        {(["presencial", "virtual", "ambas"] as const).map((mod) => (
                          <button
                            key={mod}
                            type="button"
                            onClick={() => setModalidad(mod)}
                            className={`py-2.5 px-3 rounded text-xs font-mono uppercase tracking-wider transition-colors border ${
                              modalidad === mod
                                ? "bg-gold/20 text-gold border-gold"
                                : "bg-bg-main border-gold/20 text-text-muted hover:text-text-pure"
                            }`}
                          >
                            {mod === "presencial" ? "Presencial (Cusco)" : mod === "virtual" ? "Virtual" : "Ambas"}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-mono uppercase text-gold/80 mb-2">¿Qué autor o tema te gustaría leer?</label>
                      <textarea
                        value={interesLectura}
                        onChange={(e) => setInteresLectura(e.target.value)}
                        rows={3}
                        placeholder="Comparte tus autores de interés o lo que esperas del Círculo..."
                        className="w-full px-4 py-3 bg-bg-main border border-gold/20 focus:border-gold text-text-pure text-sm outline-none rounded font-light"
                      />
                    </div>

                    {/* Casilla obligatoria */}
                    <label className="flex items-start gap-3 cursor-pointer select-none pt-2">
                      <input
                        type="checkbox"
                        checked={aceptaPolitica}
                        onChange={(e) => setAceptaPolitica(e.target.checked)}
                        required
                        className="mt-1 h-4 w-4 rounded border-gold/30 bg-bg-main text-gold focus:ring-gold"
                      />
                      <span className="text-xs text-text-muted font-light leading-relaxed">
                        Acepto la{" "}
                        <Link href="/privacidad" className="text-gold underline hover:text-gold-light">
                          política de privacidad
                        </Link>{" "}
                        y autorizo el tratamiento de mis datos personales para coordinar las actividades del Círculo conforme a la Ley N.° 29733 de Protección de Datos Personales del Perú.
                      </span>
                    </label>

                    {errorMessage && (
                      <p className="text-red-400 text-xs text-center font-light">{errorMessage}</p>
                    )}

                    <div className="text-center pt-4">
                      <button
                        type="submit"
                        disabled={formStatus === "loading"}
                        className="w-full sm:w-auto bg-gold text-bg-main hover:bg-gold-light hover:shadow-xl hover:shadow-gold/20 px-12 py-4 font-sans font-semibold text-xs uppercase tracking-widest transition-all duration-300 rounded disabled:opacity-50"
                      >
                        {formStatus === "loading" ? "Enviando Registro..." : "Registrarme al Círculo (100% Gratis)"}
                      </button>
                    </div>
                  </form>
                )}
              </section>
            </FadeIn>

            {/* BOTÓN COMPARTIR POR WHATSAPP */}
            <FadeIn delay={0.4}>
              <div className="text-center p-8 rounded-xl border border-gold/15 bg-bg-secondary/20 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="text-left">
                  <h3 className="font-serif text-xl text-text-pure font-light mb-1">¿Conoces a alguien a quien le interese leer con rigor?</h3>
                  <p className="text-text-muted text-xs font-light">Invita a un amigo o compañero de estudios a sumarse al Círculo de Estudio.</p>
                </div>
                <button
                  onClick={handleShareWhatsApp}
                  className="inline-flex items-center gap-2 border border-gold/40 bg-gold/5 hover:bg-gold/15 text-gold text-xs font-mono uppercase px-6 py-3 rounded tracking-wider whitespace-nowrap transition-colors"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.007c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86.174.086.275.072.376-.044.101-.116.433-.506.549-.68.116-.173.231-.145.39-.086s1.011.477 1.184.564.289.13.332.203c.044.072.044.42-.1.825z" />
                  </svg>
                  <span>Compartir por WhatsApp</span>
                </button>
              </div>
            </FadeIn>

            {/* PREGUNTAS FRECUENTES (FAQ) */}
            <FadeIn delay={0.45}>
              <section>
                <div className="text-center flex flex-col items-center mb-12">
                  <span className="text-[10px] md:text-xs font-semibold tracking-[0.4em] text-gold uppercase">
                    Claridad
                  </span>
                  <h2 className="font-serif text-3xl sm:text-4xl text-text-pure mt-3 font-light">
                    Preguntas Frecuentes
                  </h2>
                  <div className="w-12 h-[1px] bg-gold/50 mt-4" />
                </div>

                <div className="space-y-4 max-w-3xl mx-auto">
                  {siteConfig.circulo.faqs.map((faq, i) => (
                    <div
                      key={i}
                      className="p-6 rounded-xl border border-gold/15 bg-bg-secondary/20 hover:border-gold/30 transition-colors text-left"
                    >
                      <h3 className="font-serif text-lg text-text-pure font-light mb-2">
                        {faq.pregunta}
                      </h3>
                      <p className="text-text-muted text-sm font-light leading-relaxed">
                        {faq.respuesta}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            </FadeIn>

          </div>
        </main>

        <Footer />
      </div>
    </PageTransition>
  );
}
